// Skills v2 — inline "Preview impact" panel for the edit → measure → publish
// loop (B). Lets an author measure the CURRENT draft body against a golden set
// BEFORE publishing — the parity with prompt tweaking. Pick an agent + dataset,
// hit Preview impact, and see baseline vs with-skill accuracy + the delta. The
// draft is injected transiently server-side (nothing persisted).
//
// `getBody()` returns the live editor content at click time, so the measurement
// always reflects the latest edit.
import { useEffect, useState } from "react";
import { COLORS } from "../../theme/colors";
import { skillsApi, agentApi, observabilityApi } from "../../api/client";

const BORDER = COLORS.border || "#e2e5eb";
const DIM = COLORS.textDim || "#9090a8";
const MUTED = COLORS.textMuted || "#6b7280";
const GREEN = COLORS.green || "#10b981";
const RED = COLORS.red || "#ef4444";

export function unwrap(res) {
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  return [];
}

const pct = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : `${Math.round(Number(v) * 100)}%`);

// Pure: turn the raw preview result into a display verdict. Extracted for tests.
export function summarizeImpact(r) {
  if (!r) return null;
  const base = Number(r.baseline_accuracy);
  const withS = Number(r.with_skill_accuracy);
  const delta = Number(r.delta);
  const tone = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  const arrow = tone === "up" ? "▲" : tone === "down" ? "▼" : "▬";
  const errored = r.errored ?? 0;
  return {
    baseline: pct(base), withSkill: pct(withS),
    deltaLabel: `${delta > 0 ? "+" : ""}${Math.round(delta * 100)}%`,
    tone, arrow, scored: r.scored ?? 0, errored,
    erroredSuffix: errored ? ` · ${errored} errored (excluded)` : "",
  };
}

const selStyle = {
  fontSize: 12.5, color: COLORS.text, background: COLORS.surface,
  border: `1px solid ${BORDER}`, borderRadius: 8, padding: "7px 10px", maxWidth: 220,
};

export default function SkillPreviewImpactPanel({ getBody }) {
  const [agents, setAgents] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [datasetId, setDatasetId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [ag, ds] = await Promise.all([agentApi.list(), observabilityApi.listDatasets()]);
        if (!alive) return;
        const agl = unwrap(ag), dsl = unwrap(ds);
        setAgents(agl);
        setDatasets(dsl);
        if (agl[0]) setAgentId((p) => p || agl[0].id);
        if (dsl[0]) setDatasetId((p) => p || dsl[0].id);
      } catch {
        if (alive) setError("Couldn't load agents or golden sets.");
      }
    })();
    return () => { alive = false; };
  }, []);

  const run = async () => {
    const body = (getBody && getBody()) || "";
    if (!agentId || !datasetId) { setError("Pick an agent and a golden set first."); return; }
    if (!body.trim()) { setError("Nothing to measure yet — the skill body is empty."); return; }
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await skillsApi.previewCandidateImpact(agentId, { datasetId, bodyMd: body });
      setResult(res && res.data ? res.data : res);
    } catch (e) {
      if (e?.status === 412) setError("Connect a chat model in Admin → API Keys first — the eval needs one.");
      else if (e?.status === 422) setError(e?.message || "That draft isn't valid, or the golden set has no usable items.");
      else setError(e?.message || "Couldn't run the preview just now.");
    } finally {
      setBusy(false);
    }
  };

  const s = summarizeImpact(result);
  // Compute option lists + delta styling in JS (not inline JSX) so v8 can
  // line-attribute the empty-list and tone branches for the coverage gate.
  const agentOptions = agents.length === 0
    ? [<option key="_none" value="">No agents</option>]
    : agents.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>);
  const datasetOptions = datasets.length === 0
    ? [<option key="_none" value="">No golden sets</option>]
    : datasets.map((d) => <option key={d.id} value={d.id}>{d.name || d.id}</option>);
  const deltaColor = s && s.tone === "up" ? GREEN : s && s.tone === "down" ? RED : MUTED;

  return (
    <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 12, border: `1px solid ${BORDER}`, background: COLORS.surface }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: DIM, marginBottom: 8 }}>
        ▶ Preview impact — measure this draft before publishing
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <select aria-label="Agent under test" value={agentId} onChange={(e) => setAgentId(e.target.value)} style={selStyle}>
          {agentOptions}
        </select>
        <select aria-label="Golden set" value={datasetId} onChange={(e) => setDatasetId(e.target.value)} style={selStyle}>
          {datasetOptions}
        </select>
        <button
          type="button"
          onClick={run}
          disabled={busy}
          style={{
            background: COLORS.accent, color: "#fff", border: "none", borderRadius: 8,
            padding: "8px 13px", fontSize: 12.5, fontWeight: 700,
            cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1, whiteSpace: "nowrap",
          }}
        >{busy ? "Measuring…" : "▶ Preview impact"}</button>
      </div>

      {datasets.length === 0 && (
        <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
          Build a golden set first in <b>Observability → Datasets</b> — the eval scores the draft against it.
        </div>
      )}

      {error && <div style={{ fontSize: 12.5, color: RED, marginTop: 10 }}>{error}</div>}

      {s && (
        <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <Metric label="Baseline" value={s.baseline} />
          <span style={{ color: DIM }}>→</span>
          <Metric label="With this skill" value={s.withSkill} />
          <span style={{ fontSize: 14, fontWeight: 800, color: deltaColor }}>{`${s.arrow} ${s.deltaLabel}`}</span>
          <span style={{ fontSize: 11.5, color: DIM }}>{`${s.scored} scored${s.erroredSuffix}`}</span>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <span style={{ display: "inline-flex", flexDirection: "column" }}>
      <span style={{ fontSize: 18, fontWeight: 800, color: COLORS.text }}>{value}</span>
      <span style={{ fontSize: 10.5, color: DIM }}>{label}</span>
    </span>
  );
}
